import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  STORAGE_KEY,
  catalogs,
  defaultLocale,
  detectLocale,
  locales,
  type Locale,
  type TranslationCatalog,
} from './i18n/index.js';

interface LocaleContextValue {
  readonly locale: Locale;
  readonly t: TranslationCatalog;
  readonly setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/** BCP 47 tags for Intl formatting per app locale. */
export const INTL_TAGS: Record<Locale, string> = {
  en: 'en-AU',
  es: 'es',
  'zh-hans': 'zh-Hans',
  ar: 'ar',
};

function initialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored !== null && (locales as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
  } catch {
    // Storage unavailable (privacy mode) — fall through to detection.
  }
  return detectLocale(window.navigator.languages ?? [window.navigator.language]);
}

export function LocaleProvider({ children }: { readonly children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    typeof window === 'undefined' ? defaultLocale : initialLocale(),
  );

  useEffect(() => {
    const catalog = catalogs[locale];
    document.documentElement.lang = locale === 'zh-hans' ? 'zh-Hans' : locale;
    document.documentElement.dir = catalog.dir;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort.
    }
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, t: catalogs[locale], setLocale }),
    [locale, setLocale],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (context === null) throw new Error('useLocale must be used inside LocaleProvider');
  return context;
}

/**
 * The locale context, or `null` outside a provider.
 *
 * The outermost error boundary (issue #376) renders when `LocaleProvider`
 * itself failed, so its fallback cannot call `useLocale` — that would throw
 * again. This returns the context when it exists and nothing when it does not.
 */
export function useOptionalLocale(): LocaleContextValue | null {
  return useContext(LocaleContext);
}

/**
 * The catalogue for the reader's locale, resolved WITHOUT a provider.
 *
 * Used only by the outermost error boundary (issue #376), which renders when
 * `LocaleProvider` itself threw. It must therefore not depend on that provider
 * having mounted — and it cannot simply read `document.documentElement.lang`,
 * because the provider writes that language in an EFFECT that a throw during
 * the initial render prevents from running. A Spanish reader would then get
 * the English 500 page on exactly the root-failure path (cross-review F001).
 *
 * So it resolves the locale the same way `LocaleProvider` does — the stored
 * selection first, then the browser's own languages — and only falls back to
 * the document's declared language (and finally English) when the browser
 * globals are unavailable.
 */
export function fallbackCatalogue(): TranslationCatalog {
  if (typeof window !== 'undefined') {
    return catalogs[initialLocale()];
  }
  const lang = typeof document === 'undefined' ? 'en' : document.documentElement.lang || 'en';
  return catalogs[detectLocale([lang])];
}

export function useFormatters() {
  const { locale } = useLocale();
  const tag = INTL_TAGS[locale];
  return useMemo(
    () => ({
      date: new Intl.DateTimeFormat(tag, { dateStyle: 'medium' }),
      dateTime: new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }),
      number: new Intl.NumberFormat(tag),
    }),
    [tag],
  );
}
